import { Box } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import Navbar from './common/components/Navbar/Navbar';
import { useAuth } from './customHooks/useAuth';
import Routes from './routes/routes';
import theme from './theme';
import { useDiagramAuthRedirect } from './common/hooks/useDiagramAuthRedirect';

import './assets/styles/layout.css';
import Overlay from './common/components/Overlay';
import DiagramBpmnIndex from './features/BpmnIoDiagram/Index';
import { closeDiagram } from './features/BpmnIoDiagram/store/diagramViewerSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from './store';
import { DiagramViewerState } from './features/BpmnIoDiagram/store/diagramViewerInterface';
import { useOpportunityAuthRedirect } from './common/hooks/useOpportunityAuthRedirect';
import Loader from './common/components/Loader';
// import CookieBanner from './common/components/CookiesBanner';

const AppLayout: React.FC = () => {
  const location = useLocation();
  const isAuthenticated = useAuth();
  const isXPLR = location.pathname.includes('/design-nexus');

  // Detect diagram deep links and enforce authentication while preserving
  // the intended target so users can be redirected back after login.
  useDiagramAuthRedirect();

  useOpportunityAuthRedirect();

  // Set body data-path attribute for CSS targeting
  useEffect(() => {
    document.body.setAttribute('data-path', location.pathname);
  }, [location.pathname]);

  const showNavbarOnRoutes = [
    '/industry',
    '/process',
    '/process-xplr',
    '/solution-xplr',
    '/ideation-xplr',
    '/portfolio',
    '/design-nexus',
    '/diagram',
    '/',
    '/enterprise-context',
    '/approval-requests',
    '/ideation-coi/:id',
    '/ideation-chatbot/:id',
    '/opportunity/:id',
    '/data-xplr',
    '/data-xplr/:id',
    '/opportunity/:id/:diagramId',
  ];

  const shouldShowNavbar =
    isAuthenticated &&
    showNavbarOnRoutes.some((route) => {
      if (route.includes(':')) {
        // replace ALL route params like :id, :diagramId, etc.
        const regexPattern = '^' + route.replace(/:[^/]+/g, '[^/]+') + '$';
        const regex = new RegExp(regexPattern);
        return regex.test(location.pathname);
      }
      return location.pathname === route;
    });

  const dispatch = useDispatch();
  const { isOpen = false } = useSelector<RootState, DiagramViewerState>(
    (state) => state.diagramViewer
  );

  const isSolutionPageLoading = useSelector(
    (state: RootState) => state.solutionXPLR.isSolutionPageLoading ?? false
  );
  const isFullPageLoading = isSolutionPageLoading && location.pathname === '/solution-xplr';
  const effectiveShouldShowNavbar = shouldShowNavbar && !isFullPageLoading;

  useEffect(() => {
    if (isOpen) {
      dispatch(closeDiagram());
    }
  }, [location.pathname]);

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: `calc(100vh - ${effectiveShouldShowNavbar ? '60px' : '0px'})`,
          marginTop: `${effectiveShouldShowNavbar ? '60px' : '0px'}`,
          overflowX: 'hidden',
          backgroundColor: 'background.default',
        }}
      >
        {effectiveShouldShowNavbar && <Navbar />}

        <Box
          component="main"
          className={isXPLR ? 'no-scroll' : ''}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            scrollbarWidth: 'none',
            marginTop: 0,
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {/* Toaster and Providers are now mounted once in main.tsx */}
          <Routes />
        </Box>

        {/* Full-viewport overlay while a solution is being loaded from URL params.
            Routes remains mounted so NewSolutionXPLR can finish fetching and
            dispatch setSolutionPageLoading(false) to dismiss this overlay. */}
        {isFullPageLoading && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              zIndex: 1400,
              backgroundColor: 'background.default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Loader message="Loading solution..." fullScreen={false} />
          </Box>
        )}

        <Overlay open={isOpen} onClose={() => dispatch(closeDiagram())} showCloseButton={false}>
          <DiagramBpmnIndex />
        </Overlay>
        {/* <CookieBanner /> */}
      </Box>
    </ThemeProvider>
  );
};

export default AppLayout;
